import { useMemo } from 'react'

import { useStore } from '@/src/types/store'

export type PlanCode = 'essentials' | 'intelligence' | 'relait_360'
export type PublicSurface = 'investor_centre' | 'landing_page'

/**
 * Project Boston tier engine — the single source of truth for "what can this
 * tenancy do".
 *
 * Before this existed, tier was re-derived at 14 call sites across 8 files
 * using five different idioms, each of which had to re-decide what an
 * unresolved plan means while /theme is in flight. That decision now lives
 * here, once.
 *
 * Unresolved (theme still loading, or an older cached payload with no plan)
 * deliberately resolves in two *different* directions, because the safe
 * default is not the same for both questions:
 *
 *   - Capability flags (hasRegistry, hasInvestorCentre, is360) default to
 *     PERMISSIVE. Hiding a feature a tenant paid for, even for one frame, is
 *     worse than showing it briefly. This matches the pre-existing
 *     `!plan || plan === 'relait_360'` convention.
 *   - isLandingTenant defaults to FALSE. Serving the landing page to a 360
 *     tenant — replacing their public investor centre — is the destructive
 *     direction, so it must never happen on a guess.
 *
 * Callers that must not act until the answer is real should branch on
 * `isResolved` rather than inventing their own fallback.
 */
export interface Tier {
  /** Normalised plan. Legacy/unknown codes (e.g. 'premium') map to relait_360. */
  plan: PlanCode
  /** Raw surface from /theme; undefined until the theme resolves. */
  surface: PublicSurface | undefined
  /** True once /theme has answered. Use before acting on a destructive branch. */
  isResolved: boolean
  isEssentials: boolean
  isIntelligence: boolean
  is360: boolean
  /**
   * This tenancy's public root is the branded landing page, not an investor
   * centre. Surface-derived, never plan-derived: legacy plans resolve to the
   * 360 profile server-side and keep their IC. False while unresolved.
   */
  isLandingTenant: boolean
  /** Essentials has no registry connection, so registry-derived axes are meaningless. */
  hasRegistry: boolean
  /** Only 360 serves a public investor centre. */
  hasInvestorCentre: boolean
  /**
   * The public signup form may register new contacts. False on Intelligence,
   * true on Essentials and 360. Backend-supplied; an older payload that omits
   * it resolves permissive, like the other capability flags.
   */
  canWidgetSignup: boolean
  /**
   * Investor accounts can log in. True only on 360. Backend-supplied; an
   * older payload that omits it resolves permissive.
   */
  canInvestorLogin: boolean
}

const KNOWN_PLANS: readonly string[] = ['essentials', 'intelligence', 'relait_360']

/**
 * Pure resolver, exported for tests and for the non-React call sites that
 * read the theme payload directly.
 */
export const resolveTier = (
  rawPlan: string | null | undefined,
  rawSurface: string | null | undefined,
  rawWidgetSignup?: boolean | null,
  rawInvestorLogin?: boolean | null
): Tier => {
  const isResolved = rawPlan != null || rawSurface != null

  // Anything we don't recognise — including legacy 'premium' and any tier
  // added to the backend before the frontend knows about it — is the full
  // product. Never degrade a tenant because of an unfamiliar string.
  const plan: PlanCode =
    rawPlan != null && KNOWN_PLANS.includes(rawPlan) ? (rawPlan as PlanCode) : 'relait_360'

  const surface: PublicSurface | undefined =
    rawSurface === 'landing_page' || rawSurface === 'investor_centre'
      ? (rawSurface as PublicSurface)
      : undefined

  const isEssentials = plan === 'essentials'
  const isIntelligence = plan === 'intelligence'
  const is360 = plan === 'relait_360'

  return {
    plan,
    surface,
    isResolved,
    isEssentials,
    isIntelligence,
    is360,
    // Destructive direction: only ever true on a real answer.
    isLandingTenant: surface === 'landing_page',
    // Permissive direction: unresolved keeps everything visible.
    hasRegistry: !isResolved || !isEssentials,
    hasInvestorCentre: surface ? surface === 'investor_centre' : !isResolved || is360,
    // Permissive direction again: a payload predating these flags must not
    // remove a signup form or a login the tenant already has.
    canWidgetSignup: rawWidgetSignup !== false,
    canInvestorLogin: rawInvestorLogin !== false
  }
}

/**
 * Subscribes to the two primitive theme fields separately so the component
 * re-renders on a genuine tier change and not on every unrelated store write.
 */
export const useTier = (): Tier => {
  const plan = useStore(s => s.homeTheme?.plan)
  const surface = useStore(s => s.homeTheme?.public_surface)
  const widgetSignup = useStore(s => s.homeTheme?.widget_signup)
  const investorLogin = useStore(s => s.homeTheme?.investor_login)

  return useMemo(
    () => resolveTier(plan, surface, widgetSignup, investorLogin),
    [plan, surface, widgetSignup, investorLogin]
  )
}
